import React, { useState, useCallback, useEffect } from 'react';
import { Shield, Info, Loader2, FileDown, Mail, Download, AlertTriangle, Trash2, Cloud, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../services/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';

const SYNC_KEYS = [
  'gym_notebook_walking_session_history',
  'gym_notebook_walking_daily_data',
  'gym_notebook_walking_routines',
  'gym_notebook_walking_stride_length',
  'gym_notebook_meal_times',
  'gym_notebook_nutrition_goals',
  'gym_notebook_waist_history',
  'gym_notebook_body_measurements_history',
  'gym_notebook_persistent_bodyweight',
  'gym_notebook_persistent_weight_unit',
  'gym_notebook_persistent_height',
  'gym_notebook_persistent_height_unit',
  'gym_notebook_persistent_age',
  'gym_notebook_persistent_gender',
  'gym_notebook_persistent_measurements',
  'gym_notebook_waist',
  'gym_notebook_waist_unit',
  'gym_notebook_weight_goal',
  'gym_notebook_weight_goal_start',
  'medications_enabled',
];

function collectAll() {
  const data = {};
  SYNC_KEYS.forEach(k => {
    const v = localStorage.getItem(k);
    if (v !== null) data[k] = v;
  });
  const nutrition = {};
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith('nutrition_') && key.length <= 25) {
      nutrition[key] = localStorage.getItem(key);
    }
  }
  return { data, nutrition };
}

function DataManagementSection() {
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [counts, setCounts] = useState({ keys: 0, nutritionDays: 0 });

  const refreshCounts = useCallback(() => {
    const { data, nutrition } = collectAll();
    setCounts({ keys: Object.keys(data).length, nutritionDays: Object.keys(nutrition).length });
  }, []);

  useEffect(() => { refreshCounts(); }, [refreshCounts]);

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const { data, nutrition } = collectAll();
      await api.post('/sync/cloud', { data, nutrition });
      setLastSync(new Date().toLocaleTimeString());
      toast.success('Data synced to cloud');
    } catch {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleClear = () => {
    if (!window.confirm('Clear ALL local data? This removes nutrition, walking, and body data from this device. Cloud data is not affected.')) return;
    SYNC_KEYS.forEach(k => localStorage.removeItem(k));
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith('nutrition_')) localStorage.removeItem(key);
    }
    refreshCounts();
    toast.success('Local data cleared');
  };

  return (
    <div className="space-y-3">
      <h4 className="text-white font-medium flex items-center gap-2">
        <Cloud className="h-4 w-4 text-cyan-400" />
        Data Management
      </h4>
      <div className="p-3 bg-slate-800/50 rounded-xl space-y-3">
        <p className="text-sm text-slate-400">
          Your data syncs automatically across devices when you log in.
        </p>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span>{counts.keys} settings</span>
          <span>{counts.nutritionDays} nutrition days</span>
          {lastSync && <span>Synced at {lastSync}</span>}
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleSyncNow}
            disabled={syncing}
            className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
            data-testid="sync-now-btn"
          >
            {syncing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Cloud className="h-4 w-4 mr-2" />}
            {syncing ? 'Syncing...' : 'Sync Now'}
          </Button>
          <Button
            onClick={handleClear}
            variant="outline"
            className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/20"
            data-testid="clear-local-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Clear Local
          </Button>
        </div>
      </div>
    </div>
  );
}

export const DataPrivacyDialog = ({
  loadingDataSummary,
  dataSummary,
  onLoadDataSummary,
  downloadingData,
  onDownloadAllData,
  emailingPolicy,
  onEmailGdprPolicy,
  onDownloadGdprPdf,
  deletingData,
  onDeleteAllData
}) => {
  return (
    <Dialog onOpenChange={(open) => { if (open) onLoadDataSummary(); }}>
      <DialogTrigger asChild>
        <button className="flex flex-col items-center gap-2 p-4 bg-slate-800/50 rounded-2xl hover:bg-slate-800 transition-colors border border-slate-700/50" data-testid="data-privacy-btn">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            <Shield className="h-5 w-5 text-white" />
          </div>
          <span className="text-sm text-white">Data & Privacy</span>
        </button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Shield className="h-4 w-4 text-white" />
            </div>
            Data & Privacy
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          {/* Your Data Summary */}
          {loadingDataSummary ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : dataSummary && (
            <div className="p-4 bg-slate-800/50 rounded-xl">
              <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                <Info className="h-4 w-4 text-blue-400" />
                Your Data Summary
              </h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-slate-400">Nutrition Days:</div>
                <div className="text-white">{dataSummary.nutrition_days || 0}</div>
                <div className="text-slate-400">Walking Days:</div>
                <div className="text-white">{dataSummary.walking_days || 0}</div>
                <div className="text-slate-400">Walking Sessions:</div>
                <div className="text-white">{dataSummary.walking_sessions || 0}</div>
                <div className="text-slate-400">AI Plans:</div>
                <div className="text-white">{dataSummary.ai_plans || 0}</div>
                <div className="text-slate-400">Saved Meals:</div>
                <div className="text-white">{dataSummary.saved_meals || 0}</div>
                <div className="text-slate-400">Progress Photos:</div>
                <div className="text-white">{dataSummary.progress_photos || 0}</div>
              </div>
            </div>
          )}

          {/* Data Management (Cloud Sync + Clear) */}
          <DataManagementSection />
          
          {/* Privacy Policy Section */}
          <div className="space-y-3 pt-2 border-t border-slate-700">
            <h4 className="text-white font-medium pt-2">Privacy Policy (GDPR)</h4>
            <div className="p-4 bg-indigo-500/10 border border-indigo-500/30 rounded-xl">
              <p className="text-slate-400 text-sm">
                Under GDPR, you have full control over your personal data. You can access, export, or delete all your information at any time.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={onDownloadGdprPdf}
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                <FileDown className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
              <Button
                onClick={onEmailGdprPolicy}
                disabled={emailingPolicy}
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                {emailingPolicy ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Mail className="h-4 w-4 mr-2" />
                )}
                Email to Me
              </Button>
            </div>
          </div>
          
          {/* Download All Data */}
          <div className="space-y-3 pt-2 border-t border-slate-700">
            <h4 className="text-white font-medium pt-2">Download All My Data</h4>
            <p className="text-slate-400 text-sm">
              Export all your personal data in a portable format (ZIP file with JSON files).
            </p>
            <Button
              onClick={onDownloadAllData}
              disabled={downloadingData}
              className="w-full bg-indigo-600 hover:bg-indigo-500"
              data-testid="download-all-data-btn"
            >
              {downloadingData ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Download All My Data (ZIP)
            </Button>
          </div>
          
          {/* Delete All Data */}
          <div className="space-y-3 pt-2 border-t border-slate-700">
            <h4 className="text-red-400 font-medium pt-2 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Delete All My Data
            </h4>
            <p className="text-slate-400 text-sm">
              Permanently delete all your personal data from our servers. This action cannot be undone.
            </p>
            <Button
              onClick={onDeleteAllData}
              disabled={deletingData}
              variant="destructive"
              className="w-full bg-red-600 hover:bg-red-500"
              data-testid="delete-all-data-btn"
            >
              {deletingData ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Delete All My Data
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DataPrivacyDialog;
